import React, { useState, useEffect, useCallback } from 'react';
import { useAuth, useTrip, RelatedDocumentsSection, RelatedEntitiesSection, EntityLinkerModal, LinkEntitiesButton, GenericConfirmModal, entityLinkingService, WaitingIndicator, linkPresentationService } from '@trip-planner-monorepo/shared';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { placesService } from '@trip-planner-monorepo/places';
import { FaMapMarkerAlt, FaEdit, FaPlus, FaFile, FaRobot, FaSpinner, FaThumbsUp, FaThumbsDown, FaTrash, FaChevronDown, FaChevronUp, FaSearch, FaLink } from 'react-icons/fa';
import { ResourceModal } from '@trip-planner-monorepo/shared';
import { UnsavedChangesPrompt } from '@trip-planner-monorepo/shared';
import { useUnsavedChanges } from '@trip-planner-monorepo/shared';
import { Notes } from '@trip-planner-monorepo/shared';
import { cleanNotesContent } from '@trip-planner-monorepo/shared';
import { useDays } from '@trip-planner-monorepo/shared';
import { format } from 'date-fns';
import UploadModal from '@trip-planner-monorepo/documents/components/UploadModal';
import { aiResourceGenerationService, clearAIResourceCache } from '../services/aiResourceGenerationService';
import {
  markResourceAsDeleted,
  getDeletedResources,
  reinstateResource,
  loadDeletedResourcesFromFirebase,
  markResourceAsDeletedWithPersistence,
  reinstateResourceWithPersistence,
  filterDeletedResources
} from '../services/resourceDeletionService';
import { saveAIResources, getAIResources } from '@trip-planner-monorepo/shared/firebase/firestoreService';
import { EntityDetailLayout } from '@trip-planner-monorepo/shared';
import AIResourcesModal from './AIResourcesModal';
import EditPlaceModal from './PlaceDetails/EditPlaceModal';
import './PlaceDetails.css';

/**
 * Renders the "Related Days" section on the place details page.
 * Groups days by the accommodation that covers them and displays
 * "Related Days for [Accommodation Name]" with clickable accommodation links.
 *
 * Registry-aware (Issue 6): on registry-only trips, place.contentBlocks is empty —
 * the whole point of registry-only is to stop writing them — so this section resolves
 * hotels `located_in` this place from the registry, then each hotel's `stays_on` days,
 * and merges that with any legacy contentBlocks groups so nothing is shown twice.
 *
 * @param {Object} props
 * @param {Object} props.place - The place data with contentBlocks
 * @param {string} props.tripId - Trip ID for links
 * @param {string} props.userId - effectiveUserId, for registry reads
 * @param {Array} props.days - All days from useDays hook
 */
const RelatedDaysSection = ({ place, tripId, userId, days }) => {
  const [registryGroups, setRegistryGroups] = useState({});
  const placeId = place?.id;
  const canReadRegistry = Boolean(userId && tripId && placeId);

  const loadRegistryGroups = useCallback(async () => {
    if (!canReadRegistry) {
      setRegistryGroups({});
      return;
    }
    try {
      const { models: accommodationModels } = await linkPresentationService.getRelatedEntityModels(
        userId, tripId, 'place', placeId, { surface: 'related' }
      );
      const linkedAccommodations = (accommodationModels || []).filter((m) => m.entityType === 'accommodation');

      const groups = {};
      await Promise.all(linkedAccommodations.map(async (acc) => {
        const { models: dayModels } = await linkPresentationService.getRelatedEntityModels(
          userId, tripId, 'accommodation', acc.entityId, { surface: 'itinerary' }
        );
        const dayIds = (dayModels || []).filter((m) => m.entityType === 'day').map((m) => m.entityId);
        if (dayIds.length > 0) {
          groups[`accommodation_${acc.entityId}`] = {
            sourceEntityId: acc.entityId,
            sourceEntityType: 'accommodation',
            sourceEntityName: acc.entityName,
            dayIds
          };
        }
      }));
      setRegistryGroups(groups);
    } catch (error) {
      console.error('[RelatedDaysSection] Failed to load registry stays for place:', error);
      setRegistryGroups({});
    }
  }, [canReadRegistry, userId, tripId, placeId]);

  useEffect(() => {
    loadRegistryGroups();
  }, [loadRegistryGroups]);

  // Only use contentBlocks structure - legacy arrays have been removed
  const hasContentBlocks = place.contentBlocks && place.contentBlocks.length > 0;
  const hasRegistryGroups = Object.keys(registryGroups).length > 0;

  if ((!hasContentBlocks && !hasRegistryGroups) || !days || days.length === 0) {
    return null;
  }

  // Helper function to find day IDs for a date range
  const findDayIdsForDateRange = (dateRange) => {
    if (!dateRange || !dateRange.start || !dateRange.end) return [];

    const startDate = new Date(dateRange.start);
    const endDate = new Date(dateRange.end);

    return days
      .filter(day => {
        if (!day.date) return false;
        const dayDate = new Date(day.date);
        return dayDate >= startDate && dayDate <= endDate;
      })
      .map(day => day.id);
  };

  // Group legacy content blocks by source entity (excluding document references)
  const legacyGroups = (place.contentBlocks || [])
    .filter(block => block.type !== 'document_reference')
    .reduce((acc, block) => {
      const key = `${block.sourceEntityType}_${block.sourceEntityId} `;
      if (!acc[key]) {
        acc[key] = {
          sourceEntityId: block.sourceEntityId,
          sourceEntityType: block.sourceEntityType,
          sourceEntityName: block.sourceEntityName,
          dayIds: []
        };
      }
      if (block.relatedDayIds && Array.isArray(block.relatedDayIds) && block.relatedDayIds.length > 0) {
        acc[key].dayIds.push(...block.relatedDayIds);
      } else if (block.dateRange) {
        const foundDayIds = findDayIdsForDateRange(block.dateRange);
        if (foundDayIds.length > 0) {
          acc[key].dayIds.push(...foundDayIds);
        }
      }
      return acc;
    }, {});

  // Merge: registry groups take precedence per accommodation id; legacy groups fill in
  // any accommodation the registry read didn't cover (dual_write/legacy_only trips).
  const groupedContentBlocks = { ...legacyGroups };
  Object.entries(registryGroups).forEach(([key, group]) => {
    const existing = groupedContentBlocks[`${group.sourceEntityType}_${group.sourceEntityId} `];
    if (existing) {
      existing.dayIds = Array.from(new Set([...existing.dayIds, ...group.dayIds]));
    } else {
      groupedContentBlocks[key] = group;
    }
  });

  return (
    <div className="related-days-section">
      {Object.values(groupedContentBlocks).map((group, index) => {
        // Filter days by this group's dayIds and sort by order
        const relatedDays = days
          .filter(day => group.dayIds.includes(day.id))
          .sort((a, b) => a.order - b.order);

        if (relatedDays.length === 0) return null;

        return (
          <div key={`${group.sourceEntityType}_${group.sourceEntityId}`} className="related-days-group">
            <h3>
              Related Days for{' '}
              <Link
                to={`/trips/${tripId}/accommodation/${group.sourceEntityId}`}
                className="accommodation-link"
              >
                {group.sourceEntityName}
              </Link>
            </h3>
            <div className="day-chips">
              {relatedDays.map((day) => {
                const dayNumber = day.order + 1;
                const formattedDate = day.date ? format(new Date(day.date), 'EEE d MMM yyyy') : 'Undated';
                return (
                  <Link
                    key={day.id}
                    to={`/trips/${tripId}/day/${day.id}`}
                    className="day-chip"
                  >
                    {`Day ${dayNumber} - ${formattedDate}`}
                  </Link>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

function PlaceDetails() {
  // State management
  const [place, setPlace] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  const [editedPlace, setEditedPlace] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [lastEditedTime, setLastEditedTime] = useState(null);
  const [isResourceModalOpen, setIsResourceModalOpen] = useState(false);
  const [editingResource, setEditingResource] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);

  const { currentUser } = useAuth();
  const { tripId, placeId } = useParams();
  const { effectiveUserId } = useTrip();
  const { days } = useDays(tripId);

  const [hasUnsavedDetails, setHasUnsavedDetails] = useState(false);

  // Integrate unsaved changes protection
  const {
    isPromptOpen,
    handleConfirmNavigation,
    handleCancelNavigation
  } = useUnsavedChanges(hasUnsavedDetails);

  const fetchPlace = useCallback(async () => {
    if (!currentUser || !tripId || !placeId || !effectiveUserId) return;

    setLoading(true);
    try {
      const places = await placesService.fetchPlaces(effectiveUserId, tripId);
      const foundPlace = places.find(p => p.id === placeId);
      if (!foundPlace) {
        throw new Error('Place not found');
      }
      console.log('🏛️ [PlaceDetails] Loaded place data:', {
        id: foundPlace.id,
        name: foundPlace.name,
        hasContentBlocks: foundPlace.contentBlocks && foundPlace.contentBlocks.length > 0,
        contentBlocksCount: foundPlace.contentBlocks?.length || 0,
        contentBlocks: foundPlace.contentBlocks
      });

      // Check if place needs contentBlocks sync (if no accommodation contentBlocks exist)
      const hasAccommodationContentBlocks = foundPlace.contentBlocks?.some(
        block => block.sourceEntityType === 'accommodation'
      );

      if (!hasAccommodationContentBlocks) {
        console.log('🔄 Place has no accommodation contentBlocks, attempting sync...');
        try {
          const syncedPlace = await placesService.syncPlaceContentBlocks(effectiveUserId, tripId, placeId);
          if (syncedPlace !== foundPlace) {
            console.log('🔄 Place was updated with new contentBlocks');
            setPlace(syncedPlace);
            setEditedPlace(syncedPlace);
            setLastEditedTime(new Date());
            return; // Skip setting the original place data
          }
        } catch (syncError) {
          console.error('🔄 Failed to sync place contentBlocks:', syncError);
          // Continue with original place data
        }
      }

      setPlace(foundPlace);
      setEditedPlace(foundPlace);
      setLastEditedTime(foundPlace.updatedAt ? new Date(foundPlace.updatedAt) : null);
    } catch (err) {
      setError('Failed to load place details. Please try again.');
      console.error('Error fetching place:', err);
    } finally {
      setLoading(false);
    }
  }, [currentUser, tripId, placeId, effectiveUserId]);

  useEffect(() => {
    // Scroll to top when navigating to this page
    window.scrollTo(0, 0);

    fetchPlace();
  }, [fetchPlace]);

  useEffect(() => {
    console.log('Unsaved changes state:', {
      hasUnsavedDetails,
      isPromptOpen
    });
  }, [hasUnsavedDetails, isPromptOpen]);

  // Title and context editing handlers
  const handleTitleChange = (name) => {
    setEditedPlace(prev => ({
      ...prev,
      name
    }));
    setHasUnsavedDetails(true);
  };

  const handleContextChange = (context) => {
    setEditedPlace(prev => ({
      ...prev,
      context
    }));
    setHasUnsavedDetails(true);
  };

  const handleSave = async () => {
    try {
      await placesService.updatePlace(
        effectiveUserId,
        tripId,
        placeId,
        editedPlace
      );
      setIsEditing(false);
      setHasUnsavedDetails(false);
      setLastEditedTime(new Date());
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      await fetchPlace();
    } catch (err) {
      setError('Failed to save changes. Please try again.');
      console.error('Error updating place:', err);
    }
  };

  const handleCancelDetails = () => {
    setEditedPlace(place);
    setIsEditing(false);
    setHasUnsavedDetails(false);
  };

  // Notes handling
  const handleSaveNotes = async (content) => {
    try {
      const cleanContent = cleanNotesContent(content);

      const updatedPlace = {
        ...editedPlace,
        userContent: cleanContent
      };

      await placesService.updatePlace(
        effectiveUserId,
        tripId,
        placeId,
        updatedPlace
      );

      setEditedPlace(updatedPlace);
      setLastEditedTime(new Date());

      await fetchPlace();
    } catch (err) {
      setError('Failed to save notes. Please try again.');
      console.error('Error saving notes:', err);
      throw err;
    }
  };

  // Resource management handlers
  const handleAddResource = () => {
    setEditingResource(null);
    setIsResourceModalOpen(true);
  };

  const handleEditResource = (resource) => {
    setEditingResource(resource);
    setIsResourceModalOpen(true);
  };

  const handleSaveResource = async (resourceData) => {
    const validation = validateResource(resourceData);
    if (!validation.isValid) {
      setResourceValidationError(validation.errors);
      return;
    }

    setResourceValidationError(null);
    try {
      if (editingResource) {
        await placesService.updateResource(
          effectiveUserId,
          tripId,
          placeId,
          editingResource.id,
          resourceData
        );
      } else {
        await placesService.addResource(
          effectiveUserId,
          tripId,
          placeId,
          resourceData
        );
      }
      await fetchPlace();
      setIsResourceModalOpen(false);
    } catch (error) {
      setError('Failed to save resource. Please try again.');
      throw error;
    }
  };

  const handleDeleteResource = async (resourceId) => {
    try {
      await placesService.deleteResource(
        effectiveUserId,
        tripId,
        placeId,
        resourceId
      );
      await fetchPlace();
      setIsResourceModalOpen(false);
    } catch (error) {
      setError('Failed to delete resource. Please try again.');
      throw error;
    }
  };

  /**
   * Handles opening the document upload modal
   */
  const handleDocumentUpload = () => {
    setShowUploadModal(true);
  };

  /**
   * Handles document upload completion
   */
  const handleDocumentProcessed = async (result) => {
    console.log('Document processed from place:', result);
    setShowUploadModal(false);
    // Refresh place data to show the newly uploaded document
    await fetchPlace();
  };

  /**
   * Handles entity link creation
   */
  const handleLinkCreated = async (links) => {
    console.log('Links created from place:', links);
    // Refresh place data to show the newly created links
    await fetchPlace();
  };

  const validateResource = (data) => {
    const errors = [];

    if (!data.name?.trim()) {
      errors.push('Resource name is required');
    }

    if (!data.url?.trim()) {
      errors.push('Resource URL is required');
    } else {
      try {
        new URL(data.url);
      } catch {
        errors.push('Please enter a valid URL');
      }
    }

    return {
      isValid: errors.length === 0,
      errors: errors.join('\n')
    };
  };

  const [resourceValidationError, setResourceValidationError] = useState(null);

  // AI Resource Generation State
  const [aiResources, setAiResources] = useState({});
  const [isGeneratingAIResources, setIsGeneratingAIResources] = useState(false);
  const [aiResourcesError, setAiResourcesError] = useState(null);

  // Load show/hide preference from localStorage
  const [showAIResources, setShowAIResources] = useState(() => {
    const saved = localStorage.getItem('tripPlanner_showAIResources');
    return saved !== null ? JSON.parse(saved) : true; // Default to true if not set
  });

  // Modal State
  const [showAIResourcesModal, setShowAIResourcesModal] = useState(false);
  const [deletedAIResources, setDeletedAIResources] = useState([]);

  // Unlink Modal State
  const [unlinkModalOpen, setUnlinkModalOpen] = useState(false);
  const [entityToUnlink, setEntityToUnlink] = useState(null);
  const [isUnlinking, setIsUnlinking] = useState(false);


  // URL generation helpers
  const generateMapLink = (place) => {
    if (place.latitude && place.longitude) {
      return `https://www.google.com/maps/search/?api=1&query=${place.latitude},${place.longitude}`;
    }
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(place.name + (place.address ? ', ' + place.address : ''))}`;
  };

  const generateWikiLink = (placeName) => {
    return `https://en.wikipedia.org/wiki/${encodeURIComponent(placeName)}`;
  };

  const generateGoogleSearchLink = (place) => {
    // Generate a Google search query with place name and context for better results
    const searchQuery = place.context
      ? `${place.name} ${place.context}`
      : place.name;
    return `https://www.google.com/search?q=${encodeURIComponent(searchQuery)}`;
  };

  // AI Resource Generation handler
  const generateAIResources = useCallback(async () => {
    if (isGeneratingAIResources || !editedPlace?.name) return; // Already generating or no place data

    setIsGeneratingAIResources(true);
    setAiResourcesError(null);

    try {
      console.log(`🤖 [PlaceDetails] Generating AI resources for: ${editedPlace.name}`);

      // Prepare coordinates if available
      const coordinates = (editedPlace.latitude && editedPlace.longitude) ? {
        lat: editedPlace.latitude,
        lng: editedPlace.longitude
      } : null;

      if (coordinates) {
        console.log(`📍 [PlaceDetails] Passing coordinates to AI service: ${coordinates.lat}, ${coordinates.lng}`);
      } else {
        console.warn(`⚠️ [PlaceDetails] No coordinates available for ${editedPlace.name}`);
      }

      const result = await aiResourceGenerationService.generateAIResources(
        effectiveUserId,
        tripId,
        placeId,
        editedPlace.name,
        editedPlace.context || '',  // Pass geographic context for location disambiguation
        false,  // forceRefresh
        coordinates  // Pass coordinates for Maps Grounding
      );

      // Clear cache after successful generation to ensure fresh results next time
      clearAIResourceCache(effectiveUserId, tripId, placeId);

      setAiResources(result.resources);
      console.log(`✅ [PlaceDetails] Generated AI resources:`, result.metadata);
      console.log(`🔍 [PlaceDetails] AI resources object:`, result.resources);
    } catch (error) {
      console.error('❌ [PlaceDetails] Error generating AI resources:', error);
      setAiResourcesError(error.message);
    } finally {
      setIsGeneratingAIResources(false);
    }
  }, [effectiveUserId, tripId, placeId, editedPlace?.name, isGeneratingAIResources]);

  // Load AI resources and deleted resources from Firestore on component mount
  useEffect(() => {
    if (currentUser && tripId && placeId && effectiveUserId) {
      // CRITICAL FIX: Load deletion cache FIRST, then filter AI resources
      // This prevents race condition where resources are filtered before deletions are loaded
      const loadResourcesSequentially = async () => {
        try {
          console.log(`🔍 [PlaceDetails] Starting resource load for place: ${placeId}`);

          // Step 1: Load deleted resources from Firebase (populates in-memory cache)
          const deleted = await loadDeletedResourcesFromFirebase(effectiveUserId, tripId, placeId);
          setDeletedAIResources(deleted);
          console.log(`📋 [PlaceDetails] Loaded ${deleted.length} deleted resources from Firebase`);
          console.log(`📋 [PlaceDetails] Deleted resource URLs:`, deleted.map(d => d.url));

          // Step 2: Load AI resources from Firestore (persistent storage)
          const storedResources = await getAIResources(effectiveUserId, tripId, placeId);
          console.log(`📋 [PlaceDetails] getAIResources returned:`, storedResources);
          console.log(`📋 [PlaceDetails] storedResources type:`, typeof storedResources);
          console.log(`📋 [PlaceDetails] storedResources keys:`, storedResources ? Object.keys(storedResources) : 'null');

          if (storedResources && Object.keys(storedResources).length > 0) {
            console.log(`📋 [PlaceDetails] Processing ${Object.keys(storedResources).length} resource categories`);

            // Filter out deleted resources (in-memory cache is now populated)
            const filteredResources = {};
            Object.keys(storedResources).forEach(category => {
              const categoryResources = storedResources[category];
              console.log(`📋 [PlaceDetails]   Category "${category}": ${categoryResources?.length || 0} resources before filtering`);

              const filtered = filterDeletedResources(
                effectiveUserId,
                tripId,
                placeId,
                categoryResources
              );

              filteredResources[category] = filtered;
              console.log(`📋 [PlaceDetails]   Category "${category}": ${filtered.length} resources after filtering`);
            });

            setAiResources(filteredResources);
            console.log('✅ [PlaceDetails] Set AI resources state with filtered data');
          } else {
            console.log(`⚠️ [PlaceDetails] No stored resources found, checking cache...`);
            // Fallback to cached resources if not in Firestore
            const cachedResources = aiResourceGenerationService.getCachedAIResources(
              effectiveUserId,
              tripId,
              placeId
            );
            if (Object.keys(cachedResources).length > 0) {
              setAiResources(cachedResources);
              console.log('📋 [PlaceDetails] Loaded cached AI resources (fallback)');
            } else {
              console.log('📭 [PlaceDetails] No cached resources available');
            }
          }
        } catch (error) {
          console.error('❌ [PlaceDetails] Failed to load resources from Firestore:', error);
          // Fallback to cache on error
          const cachedResources = aiResourceGenerationService.getCachedAIResources(
            effectiveUserId,
            tripId,
            placeId
          );
          if (Object.keys(cachedResources).length > 0) {
            setAiResources(cachedResources);
            console.log('📋 [PlaceDetails] Loaded cached AI resources (fallback after error)');
          }
          setDeletedAIResources([]);
        }
      };

      loadResourcesSequentially();
    }
  }, [currentUser, tripId, placeId, effectiveUserId]);

  // Handle resource deletion from modal
  const handleDeleteAIResource = useCallback((category, resourceIndex) => {
    try {
      const resource = aiResources[category]?.[resourceIndex];
      if (!resource) {
        console.error(`❌ [PlaceDetails] Resource not found at category ${category}, index ${resourceIndex}`);
        return;
      }

      console.log(`🗑️ [PlaceDetails] Deleting resource: ${resource.url}`);

      // Mark as deleted with Firebase persistence
      markResourceAsDeletedWithPersistence(
        effectiveUserId,
        tripId,
        placeId,
        resource.url,
        { name: resource.name, category: category }
      );

      // Remove from active resources
      setAiResources(prevResources => {
        const updated = { ...prevResources };
        updated[category] = updated[category].filter((_, idx) => idx !== resourceIndex);
        return updated;
      });

      // Add to deleted resources (only if not already deleted)
      setDeletedAIResources(prevDeleted => {
        // Check if this URL is already in the deleted list
        if (prevDeleted.some(r => r.url === resource.url)) {
          return prevDeleted; // Already deleted, no change
        }
        return [
          ...prevDeleted,
          {
            url: resource.url,
            name: resource.name,
            category: category,
            deletedAt: new Date().toISOString()
          }
        ];
      });

      console.log(`✅ [PlaceDetails] Resource deleted successfully`);
    } catch (error) {
      console.error('❌ [PlaceDetails] Error deleting resource:', error);
    }
  }, [effectiveUserId, tripId, placeId, aiResources]);

  // Handle resource reinstatement from modal
  const handleReinstateAIResource = useCallback(async (resource) => {
    try {
      console.log(`♻️ [PlaceDetails] Reinstating resource: ${resource.url}`);

      // Remove from deleted list in Firebase
      const reinstated = await reinstateResourceWithPersistence(effectiveUserId, tripId, placeId, resource.url);

      if (!reinstated) {
        console.error(`❌ [PlaceDetails] Failed to reinstate resource`);
        return;
      }

      // Remove from deleted resources state
      setDeletedAIResources(prevDeleted =>
        prevDeleted.filter(r => r.url !== resource.url)
      );

      console.log(`✅ [PlaceDetails] Resource reinstated - will appear in next generation`);
    } catch (error) {
      console.error('❌ [PlaceDetails] Error reinstating resource:', error);
    }
  }, [effectiveUserId, tripId, placeId]);

  // Handle AI resource generation from modal
  const handleGenerateInModal = useCallback(async () => {
    if (isGeneratingAIResources || !editedPlace?.name) return;

    setIsGeneratingAIResources(true);
    setAiResourcesError(null);

    try {
      console.log(`🤖 [PlaceDetails] Generating AI resources for: ${editedPlace.name}`);

      // Ensure deleted resources are loaded from Firebase before generation
      // This ensures the AI service has the latest deleted URLs to exclude
      const loadedDeletedResources = await loadDeletedResourcesFromFirebase(effectiveUserId, tripId, placeId);
      console.log(`📥 [PlaceDetails] Loaded ${loadedDeletedResources.length} deleted resources before generation`);

      // Prepare coordinates if available
      const coordinates = (editedPlace.latitude && editedPlace.longitude) ? {
        lat: editedPlace.latitude,
        lng: editedPlace.longitude
      } : null;

      if (coordinates) {
        console.log(`📍 [PlaceDetails] Passing coordinates to AI service: ${coordinates.lat}, ${coordinates.lng}`);
      } else {
        console.warn(`⚠️ [PlaceDetails] No coordinates available for ${editedPlace.name}`);
      }

      // Generate AI resources with forceRefresh=true (always call Gemini, bypass cache)
      const result = await aiResourceGenerationService.generateAIResources(
        effectiveUserId,
        tripId,
        placeId,
        editedPlace.name,
        editedPlace.context || '',  // Pass geographic context for location disambiguation
        true,  // forceRefresh: always generate fresh resources
        coordinates  // Pass coordinates for Maps Grounding
      );

      // Log raw results before filtering
      const totalRawResources = Object.values(result.resources).reduce(
        (sum, resources) => sum + (resources?.length || 0), 0
      );
      console.log(`📊 [PlaceDetails] AI returned ${totalRawResources} total resources (before filtering)`);

      // Filter out deleted resources from the generated results
      const filteredResources = {};
      Object.keys(result.resources).forEach(category => {
        const categoryResources = result.resources[category];
        filteredResources[category] = filterDeletedResources(
          effectiveUserId,
          tripId,
          placeId,
          categoryResources
        );
      });

      const totalFilteredResources = Object.values(filteredResources).reduce(
        (sum, resources) => sum + (resources?.length || 0), 0
      );
      console.log(`✅ [PlaceDetails] After filtering: ${totalFilteredResources} active resources`);

      setAiResources(filteredResources);

      // Save AI resources to Firestore for persistence
      await saveAIResources(effectiveUserId, tripId, placeId, filteredResources);
      console.log(`💾 [PlaceDetails] Saved AI resources to Firestore`);

      // Close modal after successful generation
      setShowAIResourcesModal(false);
    } catch (error) {
      console.error('❌ [PlaceDetails] Error generating AI resources:', error);
      setAiResourcesError(error.message);
    } finally {
      setIsGeneratingAIResources(false);
    }
  }, [effectiveUserId, tripId, placeId, editedPlace?.name, isGeneratingAIResources]);

  // Handle Unlink Request
  const handleUnlinkRequest = (entityType, entityId, entityName) => {
    setEntityToUnlink({ entityType, entityId, entityName });
    setUnlinkModalOpen(true);
  };

  // Handle Confirm Unlink
  const handleConfirmUnlink = async () => {
    if (!entityToUnlink) return;

    setIsUnlinking(true);
    try {
      await entityLinkingService.removeLink(
        effectiveUserId,
        tripId,
        'place',
        placeId,
        entityToUnlink.entityType,
        entityToUnlink.entityId
      );

      // Refresh place data
      await fetchPlace();
      setUnlinkModalOpen(false);
      setEntityToUnlink(null);
    } catch (err) {
      console.error('Error unlinking entity:', err);
      setError('Failed to unlink entity. Please try again.');
    } finally {
      setIsUnlinking(false);
    }
  };

  // Early return if still loading or no place data
  if (loading) {
    return (
      <div className="place-details">
        <WaitingIndicator message="Loading place details..." />
      </div>
    );
  }

  if (!editedPlace) {
    return (
      <div className="place-details">
        <div className="error">Place not found</div>
      </div>
    );
  }

  // Prepare contents for the layout slots

  // Header Actions
  const titleAction = (
    <button
      className="edit-button inline"
      onClick={() => setIsEditing(true)}
      title="Edit name and context"
    >
      <FaEdit />
    </button>
  );

  const primaryActions = [
    <button
      key="upload"
      className="edit-button document-upload-button"
      onClick={handleDocumentUpload}
      title="Upload Document"
    >
      <FaFile /> Document Upload
    </button>
  ];

  const secondaryActions = [
    <LinkEntitiesButton
      key="link"
      className="edit-button"
      onClick={() => setShowLinkModal(true)}
    />
  ];

  // Resources Content Slot
  const resourcesContent = (
    <div className="links-container">
      {/* 1. Default Resources */}
      <div className="resource-section">
        <h2 className="resource-section-title">Default Resources</h2>
        <div className="resource-items">
          <div className="external-link-item">
            <a
              href={generateMapLink(editedPlace)}
              className="external-link"
              target="_blank"
              rel="noopener noreferrer"
            >
              <FaMapMarkerAlt className="icon" />
              <span className="external-link-text">Google Maps</span>
            </a>
          </div>

          <div className="external-link-item">
            <a
              href={generateWikiLink(editedPlace.name)}
              className="external-link"
              target="_blank"
              rel="noopener noreferrer"
            >
              <span className="external-link-text">Wikipedia</span>
            </a>
          </div>

          <div className="external-link-item">
            <a
              href={generateGoogleSearchLink(editedPlace)}
              className="external-link"
              target="_blank"
              rel="noopener noreferrer"
            >
              <FaSearch className="icon" />
              <span className="external-link-text">Google Search</span>
            </a>
          </div>
        </div>
      </div>

      {/* 2. Custom Resources */}
      <div className="resource-section">
        <div className="resources-heading">
          <h2>
            Custom Resources
            <button
              className="add-resource-button inline"
              onClick={handleAddResource}
              title="Add your own custom resource references"
            >
              <FaPlus />
            </button>
          </h2>
        </div>
        {editedPlace.resources && editedPlace.resources.length > 0 ? (
          <div className="resource-items">
            {editedPlace.resources.map(resource => (
              <div key={resource.id} className="external-link-item">
                <a
                  href={resource.url}
                  className="external-link"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <span className="external-link-text">{resource.name}</span>
                </a>
                <button
                  className="resource-edit-button"
                  onClick={() => handleEditResource(resource)}
                >
                  <FaEdit />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="no-custom-resources-message">
            <p>No custom resources yet. Click the + button above to add your own.</p>
          </div>
        )}
      </div>

      {/* 3. AI-Generated Resources - ALWAYS show header and button */}
      <div className="resource-section">
        <div className="ai-resources-header">
          <h2
            className="resource-section-title collapsible"
            onClick={() => {
              const newValue = !showAIResources;
              setShowAIResources(newValue);
              localStorage.setItem('tripPlanner_showAIResources', JSON.stringify(newValue));
            }}
            style={{ cursor: 'pointer' }}
          >
            <FaRobot className="icon" />
            AI-Generated Resources
            {showAIResources ? <FaChevronUp className="arrow" /> : <FaChevronDown className="arrow" />}
            <span className="toggle-text">
              {showAIResources ? 'Hide' : 'Show'}
            </span>
          </h2>
          <button
            className="ai-generate-button"
            onClick={(e) => {
              e.stopPropagation();
              setShowAIResourcesModal(true);
            }}
            title="Manage AI-powered resources"
          >
            <FaRobot />
            AI Resources
          </button>
        </div>

        {/* Only show content when expanded */}
        {showAIResources && (
          <div className="ai-resources-content">
            {Object.keys(aiResources).length > 0 ? (
              <>
                {/* General Resources */}
                {aiResources.general && aiResources.general.length > 0 && (
                  <div className="ai-category">
                    <h4 className="ai-category-title">General Information</h4>
                    <div className="resource-items">
                      {aiResources.general.map(resource => (
                        <div key={resource.id} className={`external-link-item ai-resource ${resource.isSearchFallback ? 'search-fallback' : ''}`}>
                          <a
                            href={resource.url}
                            className="external-link"
                            target="_blank"
                            rel="noopener noreferrer"
                            title={resource.description || resource.name}
                          >
                            <span className="external-link-text">{resource.name}</span>
                            {resource.isSearchFallback && (
                              <FaSearch className="search-badge" />
                            )}
                          </a>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Restaurants & Cafes */}
                {aiResources.restaurants && aiResources.restaurants.length > 0 && (
                  <div className="ai-category">
                    <h4 className="ai-category-title">Restaurants & Cafes</h4>
                    <div className="resource-items">
                      {aiResources.restaurants.map(resource => (
                        <div key={resource.id} className={`external-link-item ai-resource ${resource.isSearchFallback ? 'search-fallback' : ''}`}>
                          <a
                            href={resource.url}
                            className="external-link"
                            target="_blank"
                            rel="noopener noreferrer"
                            title={resource.description || resource.name}
                          >
                            <span className="external-link-text">{resource.name}</span>
                            {resource.isSearchFallback && (
                              <FaSearch className="search-badge" />
                            )}
                          </a>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="no-ai-resources-message">
                <p>No AI-generated resources yet. Click "AI Resources" button to generate intelligent recommendations based on your trip context.</p>
              </div>
            )}
          </div>
        )}

        {/* AI Generation Error */}
        {aiResourcesError && (
          <div className="ai-error">
            <p>Failed to generate AI resources: {aiResourcesError}</p>
            <button onClick={generateAIResources} className="retry-button">
              Try Again
            </button>
          </div>
        )}
      </div>
    </div>
  );

  // Related Content Slot
  const relatedContent = (
    <div className="entity-connections">
      <RelatedDaysSection place={editedPlace} tripId={tripId} userId={effectiveUserId} days={days} />
      {/* Related Entities Section */}
      <div className="related-entities-container">
        <RelatedEntitiesSection
          contentBlocks={editedPlace.contentBlocks}
          tripId={tripId}
          userId={effectiveUserId}
          hostEntityType="place"
          hostEntityId={placeId}
          excludeTypes={['document']} // Documents are handled in RelatedDocumentsSection
          onUnlink={handleUnlinkRequest}
        />
      </div>
      <RelatedDocumentsSection
        contentBlocks={editedPlace.contentBlocks}
        tripId={tripId}
        userId={effectiveUserId}
        hostEntityType="place"
        hostEntityId={placeId}
        className="place-related-documents"
        onUnlink={handleUnlinkRequest}
      />
    </div>
  );

  // Notes Slot
  const notesContent = (
    <Notes
      content={editedPlace.userContent}
      onSave={handleSaveNotes}
      lastEditedTime={lastEditedTime}
      className="place-notes"
    />
  );

  return (
    <>
      <EntityDetailLayout
        className="place-details"
        title={editedPlace.name}
        subtitle={editedPlace.context}
        titleAction={titleAction}
        primaryActions={primaryActions}
        secondaryActions={secondaryActions}
        resourcesContent={resourcesContent}
        relatedContent={relatedContent}
        notesContent={notesContent}
        loading={loading}
        error={error}
      />

      {/* Edit Modal (New) */}
      <EditPlaceModal
        isOpen={isEditing}
        onClose={handleCancelDetails}
        onSave={async (updatedData) => {
          // Update local state primarily used for display
          setEditedPlace(updatedData);
          setPlace(updatedData); // Also update 'place' to match

          // Trigger save to backend
          try {
            await placesService.updatePlace(effectiveUserId, tripId, placeId, updatedData);
            setLastEditedTime(new Date());
            setSaved(true);
            setTimeout(() => setSaved(false), 2000);
          } catch (err) {
            setError('Failed to save changes. Please try again.');
            console.error('Error updating place:', err);
            // Revert on error?
            setEditedPlace(place);
          }
          setIsEditing(false);
        }}
        initialPlace={editedPlace}
      />

      {/* Resource Modal */}
      <ResourceModal
        isOpen={isResourceModalOpen}
        onClose={() => setIsResourceModalOpen(false)}
        onSave={handleSaveResource}
        onDelete={handleDeleteResource}
        resource={editingResource}
        section="places"
        validationError={resourceValidationError}
      />

      {/* Document Upload Modal */}
      {showUploadModal && (
        <UploadModal
          onClose={() => setShowUploadModal(false)}
          onProcessed={handleDocumentProcessed}
          currentEntity={{
            type: 'place',
            id: placeId,
            name: editedPlace.name || 'Unnamed Place'
          }}
        />
      )}

      {/* Entity Linker Modal */}
      {showLinkModal && editedPlace && (
        <EntityLinkerModal
          sourceEntity={{
            type: 'place',
            id: placeId,
            name: editedPlace.name || 'Unnamed Place'
          }}
          onLinkCreated={handleLinkCreated}
          onClose={() => setShowLinkModal(false)}
          allowMultiSelect={true}
        />
      )}

      {/* AI Resources Management Modal */}
      <AIResourcesModal
        open={showAIResourcesModal}
        onClose={() => setShowAIResourcesModal(false)}
        placeName={editedPlace?.name || 'Place'}
        placeId={placeId}
        activeResources={aiResources}
        deletedResources={deletedAIResources}
        onDelete={handleDeleteAIResource}
        onReinstate={handleReinstateAIResource}
        onGenerate={handleGenerateInModal}
        isGenerating={isGeneratingAIResources}
      />

      <UnsavedChangesPrompt
        isOpen={isPromptOpen}
        onConfirm={handleConfirmNavigation}
        onCancel={handleCancelNavigation}
      />
      {/* Unlink Confirmation Modal */}
      <GenericConfirmModal
        isOpen={unlinkModalOpen}
        onClose={() => setUnlinkModalOpen(false)}
        onConfirm={handleConfirmUnlink}
        title="Unlink Entity"
        message={
          entityToUnlink
            ? `Are you sure you want to unlink "${entityToUnlink.entityName}"? This will remove the link in both directions.`
            : 'Are you sure you want to unlink this entity?'
        }
        confirmText="Unlink"
        isDestructive={true}
        loading={isUnlinking}
      />
    </>
  );
}

export default PlaceDetails;